<template>
  <!-- 27 May 2026 JCH: Admin page for editing dropdown lists (carrier, ptype, clmstatus, etc.). -->
  <div class="edit-lists-root" :style="$_cssVars">
    <v-card class="elevation-2 rounded-0">
      <v-card-title class="py-1 px-3 flex-nowrap">
        <button-tip btnclass="ml-n1 mt-0 mr-3" @click="requestRefresh" elevation="5" fab icolor="black"
                    iname="mdi-update" tip="Refresh all lists — clears all unsaved changes" xsmall/>
        <span class="edit-lists-title">Edit Lists</span>
        <information-dialog-icon iconStyle="position: relative; top: 2px" title="Edit Lists" tclass="headline grey lighten-2 mb-4"
                                 confirmClass="mr-3 mb-1 title" confirmColor="secondary darken-1" compact-xs>
          <p class="mb-2">Pick a list from the dropdown to edit its rows.</p>
          <ul class="mb-2">
            <li><b>Edit</b> any cell, then click the row's <v-icon small>mdi-content-save-outline</v-icon> save icon — changes aren't stored until then.</li>
            <li><b>Revert</b> (<v-icon small>mdi-arrow-u-left-top</v-icon> on a saved row) undoes edits on that row only.</li>
            <li><b>Add row</b> adds a blank row pinned at the top so you can fill it in without it moving. Click the row's
              <v-icon small>mdi-content-save-outline</v-icon> save icon and wait for "Row created." — until then it's only on this screen.
              Once saved it drops into its sorted spot (rows are ordered by <b>Sort</b>, then <b>Short</b>).</li>
            <li><b>New type</b> creates a brand-new list with its first row.</li>
            <li><b>Delete</b> removes a row permanently.</li>
            <li><b>Switch list type</b> only changes what's shown — unsaved rows in other lists stay in memory until you save or switch back. Types with unsaved rows are marked in the dropdown.</li>
            <li><b>Refresh</b> (<v-icon small>mdi-update</v-icon>) reloads all lists from the server and drops every unsaved change on all lists.</li>
          </ul>
        </information-dialog-icon>
        <v-spacer/>
        <v-btn class="flex-shrink-0" color="secondary" small @click="openNewTypeDialog">
          <v-icon left small>mdi-plus</v-icon>
          <span class="d-none d-sm-inline">New type</span>
          <span class="d-sm-none">Type</span>
        </v-btn>
      </v-card-title>
    </v-card>

    <v-card class="elevation-1 rounded-0 mt-1">
      <!-- flex-nowrap pins "Add row" right; label shortens on xs; pt-3 = floating-label headroom -->
      <!-- 29 May 2026 JCH: pl-3 on xs (≈ header title indent) so "List type" isn't jammed in the corner; pl-sm-8 keeps the desktop indent. -->
      <v-row align="center" class="ma-0 pr-2 pl-2 pt-3 pb-1 flex-nowrap lists-toolbar" no-gutters>
        <!-- 4 Jun 2026 JCH: spacers sm+ only (they center the controls); on xs they'd steal slack the search field should absorb -->
        <v-spacer v-if="!$vuetify.breakpoint.xs"/>
        <v-autocomplete
          v-model="selectedType" :items="typeSelectItems" item-text="text" item-value="value"
          dense clearable label="List type" placeholder="Pick a list…"
          style="max-width: 45vw; min-width: 90px" :menu-props="{ maxHeight: 420, offsetY: true }"/>
        <v-spacer v-if="!$vuetify.breakpoint.xs"/>
        <!-- 29 May 2026 JCH: count hint under the field (like Home); 4 Jun: xs search grows into the icon-add's freed space, ml-3 gutters the dropdown -->
        <v-text-field
          v-model="rowFilter" append-icon="mdi-magnify" clearable dense
          class="ml-3 ml-sm-0 lists-search-field flex-sm-shrink-0"
          :hint="rowCountHint" persistent-hint single-line
          :label="$vuetify.breakpoint.xs ? 'Search' : 'Search rows'"
          :style="searchFieldStyle"/>
        <v-spacer v-if="!$vuetify.breakpoint.xs"/>
        <!-- 4 Jun 2026 JCH: xs icon-only add (button-tip keeps the tooltip even when disabled) frees label width for search; sm+ keeps the text button -->
        <button-tip v-if="$vuetify.breakpoint.xs" btnclass="ml-1 flex-shrink-0" @click="addBlankRow"
                    color="secondary" elevation="5" fab xsmall icolor="white" iname="mdi-plus" :disabled="!selectedType" tip="Add row"/>
        <v-btn v-else class="ml-2 flex-shrink-0" color="secondary" small :disabled="!selectedType" @click="addBlankRow">
          <v-icon left small>mdi-plus</v-icon>Add row
        </v-btn>
      </v-row>

      <v-data-table
        class="elevation-0 lists-table"
        :class="{ 'lists-flashing': flashing }"
        dense
        fixed-header
        :headers="headers"
        hide-default-footer
        item-key="_key"
        :items="displayedRows"
        :items-per-page="-1"
        :loading="loading"
        loading-text="Loading lists…"
        :mobile-breakpoint="0"
        no-data-text="No rows in this list. Click 'Add row' to start."
        :sort-by="['sort', 'short']"
        :sort-desc="[false, false]"
        multi-sort
        :custom-sort="sortRows"
        :item-class="rowClass">

        <template v-slot:item.lst_id="{ item }">
          <span class="lists-id-cell">
            <span class="lists-id-unsaved-slot"><span v-if="item._dirty" class="lists-id-unsaved">unsaved</span></span>
            <span v-if="item.lst_id" class="caption grey--text lists-id-num">{{ item.lst_id }}</span>
          </span>
        </template>

        <template v-slot:item.short="{ item }">
          <v-text-field v-model="item.short" dense hide-details single-line
                        @input="markDirty(item)" class="lists-cell-input"/>
        </template>

        <template v-slot:item.long="{ item }">
          <v-text-field v-model="item.long" dense hide-details single-line
                        @input="markDirty(item)" class="lists-cell-input"/>
        </template>

        <template v-slot:item.sort="{ item }">
          <v-text-field v-model.number="item.sort" type="number" step="1" dense hide-details single-line
                        @input="markDirty(item)" class="lists-cell-input lists-cell-input--sort"/>
        </template>

        <template v-slot:item.actions="{ item }">
          <!-- desktop: inline save + delete/discard icons (one-click, no extra tap) -->
          <template v-if="!$vuetify.breakpoint.xs">
            <button-tip @click="saveRow(item)" icon iname="mdi-content-save-outline" small
                        :disabled="!item._dirty || saving"
                        :icolor="saveIconColor(item)"
                        :btnstyle="saveIconStyle(item)"
                        :tip="item.lst_id ? 'Save changes' : 'Save to create row'"/>
            <button-tip v-if="item.lst_id && item._dirty" @click="revertRow(item)" icon
                        iname="mdi-arrow-u-left-top" small :disabled="saving" icolor="secondary darken-2"
                        tip="Revert changes"/>
            <button-tip @click="confirmDelete(item)" v-if="item.lst_id" icon
                        iname="mdi-trash-can-outline" small :disabled="saving" tip="Delete row"/>
            <button-tip @click="discardNew(item)" v-else icon
                        iname="mdi-close" small tip="Discard new row"/>
          </template>
          <!-- 29 May 2026 JCH: phones collapse the row actions into one ⋮ menu to give Short/Long the width -->
          <v-menu v-else offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on" aria-label="Row actions"><v-icon>mdi-dots-vertical</v-icon></v-btn>
            </template>
            <v-list dense>
              <v-list-item :disabled="!item._dirty || saving" @click="saveRow(item)">
                <v-list-item-icon class="mr-3"><v-icon small>mdi-content-save-outline</v-icon></v-list-item-icon>
                <v-list-item-title>{{ item.lst_id ? 'Save changes' : 'Save to create row' }}</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="item.lst_id && item._dirty" :disabled="saving" @click="revertRow(item)">
                <v-list-item-icon class="mr-3"><v-icon small>mdi-arrow-u-left-top</v-icon></v-list-item-icon>
                <v-list-item-title>Revert changes</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="item.lst_id" :disabled="saving" @click="confirmDelete(item)">
                <v-list-item-icon class="mr-3"><v-icon small>mdi-trash-can-outline</v-icon></v-list-item-icon>
                <v-list-item-title>Delete row</v-list-item-title>
              </v-list-item>
              <v-list-item v-else @click="discardNew(item)">
                <v-list-item-icon class="mr-3"><v-icon small>mdi-close</v-icon></v-list-item-icon>
                <v-list-item-title>Discard new row</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="newTypeDialog" max-width="420">
      <v-card>
        <v-card-title class="title py-3">New list type
          <v-spacer/>
          <v-btn icon class="mr-n2" @click="newTypeDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pb-2">
          <p class="caption font-italic mb-6">
            Creates a brand-new list and its first row. Use the type as the
            <code>type</code> column (e.g. <code>carrier</code>, <code>clmstatus</code>).
          </p>
          <v-text-field v-model="newTypeName" label="Type name (required)" dense class="mt-2"
                        :error-messages="newTypeError" @input="newTypeError = ''"/>
          <v-text-field v-model="newTypeShort" label="First row — Short (required)" dense/>
          <v-text-field v-model="newTypeLong" label="First row — Long" dense/>
          <v-text-field v-model.number="newTypeSort" label="First row — Sort" type="number" dense/>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer/>
          <v-btn text @click="newTypeDialog = false">Cancel</v-btn>
          <v-btn color="secondary" :disabled="!newTypeName || !newTypeShort || saving" @click="createNewType">
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="420">
      <v-card>
        <v-card-title class="title py-3">Delete row?
          <v-spacer/>
          <v-btn icon class="mr-n2" @click="deleteDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pb-2">
          <!-- 3 Jun 2026 JCH: blocked-delete swaps in-place; min-height keeps dialog size stable vs confirm text. -->
          <p class="body-2 mb-0 delete-dialog-msg">
            <span v-if="deleteError" class="delete-dialog-error">{{ deleteError }}</span>
            <span v-else>
              Delete <b>{{ deleteTarget && deleteTarget.short }}</b>
              from <b>{{ deleteTarget && deleteTarget.type }}</b>? This can't be undone.
            </span>
          </p>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer/>
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <!-- 6 Jun 2026 JCH: once blocked (deleteError set) a retry just re-hits the same FK — disable until they Cancel/close -->
          <v-btn color="error" :disabled="saving || !!deleteError" @click="doDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="refreshDialog" max-width="420">
      <v-card>
        <v-card-title class="title py-3">Refresh all lists?
          <v-spacer/>
          <v-btn icon class="mr-n2" @click="refreshDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pb-2">
          <p class="body-2 mb-0">
            Unsaved changes
            <template v-if="dirtyListTypes.length"> on <b>{{ dirtyListTypes.join(', ') }}</b></template>
            will be lost. Continue?
          </p>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer/>
          <v-btn text @click="refreshDialog = false">Cancel</v-btn>
          <v-btn color="secondary" @click="confirmRefresh">Refresh</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <snack :color="$_snack.color" :msg="$_snack.msg" v-model="$_snack.open"
           v-if="$_snack.cmpnt === 'EditLists'"/>
    <ErrorDialog ref="errorDialog"/>
  </div>
</template>

<script>
  import { ListsAPIService } from '@/servicehandlers/ListsAPIService';
  import { sortItems } from 'vuetify/lib/util/helpers';

  const listsAPIService = new ListsAPIService();

  const SAVED_FLASH_MS_LONG = 3500; // 6 Jun 2026 JCH off-screen: 2s hold + 1.5s fade (57% keyframe); MUST match .lists-row--saved-long in <style>
  const SAVED_FLASH_MS_SHORT = 2630; // 6 Jun 2026 JCH in-viewport: ~1.5s hold + fade at same 57%; MUST match .lists-row--saved-short
  const FLASH_HOVER_MS_LONG = 2500; // 6 Jun 2026 JCH suppress hover through hold + into fade on long flash
  const FLASH_HOVER_MS_SHORT = 1900; // 6 Jun 2026 JCH same ~71% ratio as long; green dim enough by here

  // 6 Jun 2026 JCH: row fully inside table scroll area ⇒ skip scroll + shorter flash (confirmation only, not wayfinding)
  const isRowInTableViewport = (rowEl, wrapperEl) => {
    if (!rowEl || !wrapperEl) return false;
    const row = rowEl.getBoundingClientRect();
    const wrap = wrapperEl.getBoundingClientRect();
    return row.top >= wrap.top && row.bottom <= wrap.bottom;
  };

  // 27 May 2026 JCH: stable client-side row id — v-data-table tracks rows whose lst_id is null until create succeeds
  let nextLocalKey = 1;
  const makeKey = () => `local-${nextLocalKey++}`;

  // 5 Jun 2026 JCH: Sort optional like Long — '' / non-numeric / null all normalize to null (not 0); shared by load/dirty/snapshot/save
  const normSort = (v) => {
    if (v === '' || v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  // 28 May 2026 JCH: Comparable snapshot of a row's editable fields, used to detect real (un)changes.
  const snapshotOf = (item) => ({
    type: item.type,
    short: item.short,
    long: item.long,
    sort: normSort(item.sort),
  });

  export default {
    name: 'EditLists',
    data() {
      return {
        rows: [],
        loading: false,
        saving: false,
        selectedType: null,
        rowFilter: '',
        flashing: false, // 6 Jun 2026 JCH: during post-save flash, suppress row-hover so a stray pointer doesn't compete with the green row

        newTypeDialog: false,
        newTypeName: '',
        newTypeShort: '',
        newTypeLong: '',
        newTypeSort: 0,
        newTypeError: '',
        deleteDialog: false,
        deleteTarget: null,
        deleteError: '', // 3 Jun 2026 JCH: inline reason shown in the delete dialog when a delete is blocked
        refreshDialog: false,
      };
    },
    computed: {
      // 3 Jun 2026 JCH: Short/Long split ~38/62; xs drops ID and slims Sort + actions (one ⋮ menu). Overflow clips — scroll inside the field
      headers() {
        const xs = this.$vuetify.breakpoint.xs;
        const cols = [
          { text: 'ID',    value: 'lst_id',  width: '80px', sortable: true, align: 'end', class: 'lists-id-col', cellClass: 'lists-id-col' },
          { text: 'Short', value: 'short',   width: '38%',                sortable: true },
          { text: 'Long',  value: 'long',    width: '62%',                sortable: true },
          { text: 'Sort',  value: 'sort',    width: xs ? '64px' : '127px', sortable: true,  align: xs ? 'center' : 'end' },
          { text: '',      value: 'actions', width: xs ? '44px' : '118px', sortable: false, align: 'center' },
        ];
        return xs ? cols.filter(c => c.value !== 'lst_id') : cols;
      },
      typesGrouped() {
        const counts = new Map();
        for (const r of this.rows) {
          const t = (r.type || '').trim();
          if (!t) continue;
          counts.set(t, (counts.get(t) || 0) + 1);
        }
        return Array.from(counts.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([type, count]) => ({ type, count }));
      },
      // 3 Jun 2026 JCH: types with any dirty row (survives switching away and back) — drives the "unsaved" dropdown tag
      dirtyListTypes() {
        return [...new Set(this.rows.filter((r) => r._dirty).map((r) => r.type))].sort();
      },
      // 28 May 2026 JCH: dropdown options — count baked into the label; ", unsaved" when the type has dirty rows
      typeSelectItems() {
        const dirtyTypes = new Set(this.dirtyListTypes);
        return this.typesGrouped.map(({ type, count }) => ({
          text: dirtyTypes.has(type) ? `${type} (${count}, unsaved)` : `${type} (${count})`,
          value: type,
        }));
      },
      rowsForSelected() {
        if (!this.selectedType) return [];
        return this.rows.filter((r) => r.type === this.selectedType);
      },
      // 5 Jun 2026 JCH: true matches for the selected type — independent of pinning, so the count stays honest.
      filteredRows() {
        return this.rowsForSelected.filter((r) => this.matchesFilter(r));
      },
      // 5 Jun 2026 JCH: rendered rows = matches ∪ dirty, so an in-progress edit never vanishes mid-type when it stops matching the search
      displayedRows() {
        if (!String(this.rowFilter || '').trim()) return this.rowsForSelected;
        return this.rowsForSelected.filter((r) => r._dirty || this.matchesFilter(r));
      },
      // 5 Jun 2026 JCH: dirty rows shown ONLY because they're pinned (don't match the search) — surfaced as "+N unsaved".
      pinnedUnsavedCount() {
        if (!String(this.rowFilter || '').trim()) return 0;
        return this.rowsForSelected.filter((r) => r._dirty && !this.matchesFilter(r)).length;
      },
      // 29 May 2026 JCH: "shown/total" under the search (mirrors Home); shown = true matches, pinned dirty rows shown as "(+N unsaved)"
      rowCountHint() {
        if (!this.selectedType) return '';
        const total = this.rowsForSelected.length;
        if (!total) return '0/0';
        if (!String(this.rowFilter || '').trim()) return `${total}/${total}`;
        const matches = this.filteredRows.length;
        return this.pinnedUnsavedCount
          ? `${matches}/${total} (+${this.pinnedUnsavedCount} unsaved)`
          : `${matches}/${total}`;
      },
      // 4 Jun 2026 JCH: xs flex-grows into the space freed by the icon-only add button; sm+ fixed width so the clearable-X can't widen it
      searchFieldStyle() {
        return this.$vuetify.breakpoint.xs
          ? { flex: '1 1 auto', minWidth: '120px' }
          : { width: 'min(40vw, 240px)', maxWidth: 'min(40vw, 240px)' };
      },
    },
    created() {
      this.loadAll();
    },
    methods: {
      // 5 Jun 2026 JCH: case-insensitive substring across visible columns; we filter ourselves (not :search) so dirty rows can be pinned in
      matchesFilter(row) {
        const q = String(this.rowFilter || '').trim().toLowerCase();
        if (!q) return true;
        return [row.short, row.long, row.sort, row.lst_id]
          .some((v) => v != null && String(v).toLowerCase().includes(q));
      },

      requestRefresh() {
        if (this.dirtyListTypes.length) this.refreshDialog = true;
        else this.doRefresh();
      },

      confirmRefresh() {
        this.refreshDialog = false;
        this.doRefresh();
      },

      // 4 Jun 2026 JCH: reload the editor + rebuild the app-wide $_lists cache (fire-and-forget) so other editors' changes show everywhere
      doRefresh() {
        this.loadAll();
        this.$_getLists();
      },

      async loadAll() {
        const keepType = this.selectedType;
        this.loading = true;
        try {
          const data = await listsAPIService.getAllLists(this.$router);
          const rows = Array.isArray(data) ? data : [];
          this.rows = rows.map((r) => {
            const base = {
              lst_id: r.lst_id,
              type: r.type || '',
              short: r.short == null ? '' : r.short,
              long: r.long == null ? '' : r.long,
              sort: normSort(r.sort),
            };
            return { _key: makeKey(), _dirty: false, _orig: snapshotOf(base), ...base }; // _orig = saved snapshot ⇒ save lights up only on real change
          });
          const types = this.typesGrouped.map((t) => t.type);
          if (keepType && types.includes(keepType)) {
            this.selectedType = keepType;
          } else if (!this.selectedType && types.length) {
            this.selectedType = types[0];
          }
        } catch (e) {
          this.$_showErrorDialog({ errorMessage: 'Failed to load lists.', forDevelopers: e });
        } finally {
          this.loading = false;
        }
      },

      // 28 May 2026 JCH: dirty = new row (no _orig) or differs from saved snapshot; revert clears it.
      markDirty(item) {
        if (!item._orig) { item._dirty = true; return; }
        const o = item._orig;
        item._dirty = item.type !== o.type
          || item.short !== o.short
          || item.long !== o.long
          || normSort(item.sort) !== o.sort;
      },

      // 5 Jun 2026 JCH: opens multi-sorted by sort,short asc. New rows (no lst_id) pin to top (newest first via unshift); existing dirty
      // rows sort by their SAVED values (_orig) so typing can't relocate the <tr> and drop focus — they reflow on save. Proxies carry the
      // frozen values, then map back to the real rows.
      sortRows(items, sortBy, sortDesc, locale, customSorters) {
        const pinned = items.filter((r) => !r.lst_id);
        const existing = items.filter((r) => r.lst_id);
        const proxies = existing.map((r) => {
          const src = (r._dirty && r._orig) ? r._orig : r;
          return { _r: r, type: src.type, short: src.short, long: src.long, sort: src.sort, lst_id: r.lst_id };
        });
        const sorted = sortItems(proxies, sortBy, sortDesc, locale, customSorters).map((p) => p._r);
        return [...pinned, ...sorted];
      },

      addBlankRow() {
        if (!this.selectedType) return;
        // 5 Jun 2026 JCH: Sort left blank — optional like Long; a blank Sort saves as blank (null), not 0.
        const row = {
          _key: makeKey(),
          _dirty: true,
          _orig: null,
          lst_id: null,
          type: this.selectedType,
          short: '',
          long: '',
          sort: null,
        };
        // 5 Jun 2026 JCH: prepend (not append) so it's visible without chasing the scroll; focus Short to type right away (also scrolls it in)
        this.rows.unshift(row);
        this.$nextTick(() => {
          // target THIS new row by _key (shared .lists-row--unsaved would grab the first dirty row in DOM order)
          const el = this.$el.querySelector(`.lists-row--key-${row._key}`);
          if (!el) return;
          el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          const input = el.querySelector('input');
          if (input) input.focus();
        });
      },

      rowClass(item) {
        // 5 Jun 2026 JCH: stamp each row with its _key so scroll/flash targets THIS row (a shared state class grabs the first DOM match)
        const key = `lists-row--key-${item._key}`;
        if (item._dirty) return `lists-row--unsaved ${key}`;
        if (item._justSaved) return `${item._savedFlashShort ? 'lists-row--saved-short' : 'lists-row--saved-long'} ${key}`; // short flash when row already on screen
        return key;
      },

      saveIconColor(item) {
        if (!item._dirty || this.saving) return 'grey';
        return 'secondary darken-2';
      },

      saveIconStyle(item) {
        if (!item._dirty || this.saving) return '';
        return 'background-color:rgba(76,95,60,.14);border-radius:50%';
      },

      discardNew(item) {
        if (item.lst_id) return;
        const i = this.rows.indexOf(item);
        if (i >= 0) this.rows.splice(i, 1);
      },

      revertRow(item) {
        if (!item.lst_id || !item._orig || !item._dirty || this.saving) return;
        const o = item._orig;
        item.type = o.type;
        item.short = o.short;
        item.long = o.long;
        item.sort = o.sort;
        item._dirty = false;
      },

      payloadFor(item) {
        return {
          type: item.type,
          short: item.short,
          long: item.long,
          sort: normSort(item.sort),
        };
      },

      async saveRow(item) {
        if (!item._dirty || this.saving) return;
        if (!item.type || !item.short) {
          this.snack('Type and Short are required.', 'error');
          return;
        }
        this.saving = true;
        try {
          const payload = this.payloadFor(item);
          const created = item.lst_id;
          if (item.lst_id) {
            await listsAPIService.updateList(item.lst_id, payload, this.$router);
            item._orig = snapshotOf(item);
            item._dirty = false;
          } else {
            const result = await listsAPIService.createCarrier(payload, this.$router);
            if (result && result.lst_id) item.lst_id = result.lst_id;
            item._orig = snapshotOf(item);
            item._dirty = false;
          }
          // 5 Jun 2026 JCH: now-clean row drops out of the filtered view if its saved value no longer matches — tell the user it's hidden
          const hidden = !item._dirty && !this.matchesFilter(item) && !!String(this.rowFilter || '').trim();
          const base = created ? 'Saved.' : 'Row created.';
          this.snack(hidden ? `${base.replace(/\.$/, '')} — hidden by your search.` : base);
          if (!hidden) this.flashSavedRow(item); // visible row: scroll to its sorted slot and flash so the user can follow it
          listsAPIService.refreshStoreListType(item.type, this.$router, this.$store);
        } catch (e) {
          this.$_showErrorDialog({ errorMessage: 'Failed to save row.', forDevelopers: e });
        } finally {
          this.saving = false;
        }
      },

      // 6 Jun 2026 JCH: measure viewport first — in-view gets ~1.5s hold + no scroll; off-screen keeps 3.5s + scrollIntoView center.
      flashSavedRow(item) {
        this.$nextTick(() => {
          const el = this.$el.querySelector(`.lists-row--key-${item._key}`);
          const wrapper = this.$el.querySelector('.lists-table .v-data-table__wrapper');
          const inView = isRowInTableViewport(el, wrapper);
          if (el && !inView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const flashMs = inView ? SAVED_FLASH_MS_SHORT : SAVED_FLASH_MS_LONG;
          const hoverMs = inView ? FLASH_HOVER_MS_SHORT : FLASH_HOVER_MS_LONG;
          this.$set(item, '_savedFlashShort', inView);
          this.$set(item, '_justSaved', true);
          setTimeout(() => { item._justSaved = false; item._savedFlashShort = false; }, flashMs); // per-item timer
          this.flashing = true;
          clearTimeout(this._flashTimer);
          this._flashTimer = setTimeout(() => { this.flashing = false; }, hoverMs); // latest save extends the window
        });
      },

      confirmDelete(item) {
        this.deleteTarget = item;
        this.deleteError = '';
        this.deleteDialog = true;
      },

      async doDelete() {
        const item = this.deleteTarget;
        if (!item || !item.lst_id || this.saving) {
          this.deleteDialog = false;
          return;
        }
        this.saving = true;
        try {
          await listsAPIService.removeList(item.lst_id, this.$router);
          const i = this.rows.indexOf(item);
          if (i >= 0) this.rows.splice(i, 1);
          this.snack('Row deleted.');
          listsAPIService.refreshStoreListType(item.type, this.$router, this.$store);
          this.deleteDialog = false;
          this.deleteTarget = null;
        } catch (e) {
          // 3 Jun 2026 JCH: ~9 FKs reference lst_id (claims + web/contract tables, NOT policies); a DB FK violation surfaces as a 500 here
          const inUse = this.looksLikeFkViolation(e);
          if (inUse) {
            // keep the dialog open and show the reason inline (snack auto-dismisses; this stays put)
            this.deleteError = 'Can\u2019t delete \u2014 still in use.';
          } else {
            this.deleteDialog = false;
            this.$_showErrorDialog({ errorMessage: 'Failed to delete row.', forDevelopers: e });
          }
        } finally {
          this.saving = false;
        }
      },

      looksLikeFkViolation(err) {
        const blob = JSON.stringify(err && (err.response || err) || '').toLowerCase();
        // 3 Jun 2026 JCH: 23503 = Postgres foreign_key_violation SQLSTATE (stable across wording/locale); word checks are a fallback
        return blob.includes('23503')
          || blob.includes('foreign key') || blob.includes('violates') || blob.includes('referenced');
      },

      openNewTypeDialog() {
        this.newTypeName = '';
        this.newTypeShort = '';
        this.newTypeLong = '';
        this.newTypeSort = 0;
        this.newTypeError = '';
        this.newTypeDialog = true;
      },

      async createNewType() {
        const name = (this.newTypeName || '').trim();
        if (!name) { this.newTypeError = 'Required'; return; }
        if (this.typesGrouped.some((t) => t.type.toLowerCase() === name.toLowerCase())) {
          this.newTypeError = 'That type already exists. Pick it on the left instead.';
          return;
        }
        this.saving = true;
        try {
          const payload = {
            type: name,
            short: this.newTypeShort,
            long: this.newTypeLong,
            sort: Number(this.newTypeSort) || 0,
          };
          const created = await listsAPIService.createCarrier(payload, this.$router);
          const newRow = {
            _key: makeKey(),
            _dirty: false,
            lst_id: created && created.lst_id ? created.lst_id : null,
            type: name,
            short: this.newTypeShort,
            long: this.newTypeLong,
            sort: Number(this.newTypeSort) || 0,
          };
          newRow._orig = newRow.lst_id ? snapshotOf(newRow) : null;
          this.rows.push(newRow);
          this.selectedType = name;
          this.newTypeDialog = false;
          this.snack(`Created list "${name}".`);
          listsAPIService.refreshStoreListType(name, this.$router, this.$store);
        } catch (e) {
          this.$_showErrorDialog({ errorMessage: 'Failed to create new list.', forDevelopers: e });
        } finally {
          this.saving = false;
        }
      },

      snack(msg, color) {
        this.$store.dispatch('SET_SNACK', {
          cmpnt: 'EditLists',
          open: true,
          msg,
          color: color || 'secondary',
        });
      },
    },
  };
</script>

<style scoped>
  /* 3 Jun 2026 JCH: app-wide table convention — $_cssVars exposes --offset (48px top-bar / 0 side-menu; see main.js); wrapper sized off it */
  .edit-lists-root { padding: 0 8px; }
  .edit-lists-title { position: relative; top: 2px; }
  /* Reserve append-inner width so clearable icon doesn't shift toolbar layout when typing */
  .lists-search-field >>> .v-input__append-inner {
    flex: 0 0 40px !important;
    min-width: 40px !important;
    max-width: 40px !important;
  }
  .delete-dialog-error { color: #b71c1c; font-size: 0.875rem; }
  .delete-dialog-msg { min-height: 2.75em; } /* ~2 lines of body-2 — confirm/error swap without height jump */
  .lists-id-cell { display: flex; align-items: center; width: 100%; }
  .lists-id-unsaved-slot { flex: 0 0 32px; min-width: 32px; overflow: hidden; } /* always reserved — stops horiz jump when label appears */
  .lists-id-unsaved { font-size: 0.5rem; line-height: 1; font-style: italic; color: #4c5f3c; white-space: nowrap; }
  .lists-id-num { margin-left: auto; }
  .lists-table >>> td.lists-id-col,
  .lists-table >>> th.lists-id-col { padding-left: 4px !important; padding-right: 4px !important; }
  .lists-table >>> tr.lists-row--unsaved { background: rgba(76, 95, 60, 0.07) !important; }
  /* 6 Jun 2026 JCH: during a flash, kill row-hover so the stray pointer can't paint a grey row beside the green one; :not() keeps green winning */
  .lists-flashing >>> .v-data-table__wrapper tbody tr:hover:not(.lists-row--unsaved):not(.lists-row--saved-long):not(.lists-row--saved-short) {
    background: transparent !important;
  }
  /* 6 Jun 2026 JCH: hold-then-fade (57% hold); durations MUST match SAVED_FLASH_MS_LONG / SAVED_FLASH_MS_SHORT */
  .lists-table >>> tr.lists-row--saved-long { animation: lists-saved-flash 3.5s ease; }
  .lists-table >>> tr.lists-row--saved-short { animation: lists-saved-flash 2.63s ease; }
  @keyframes lists-saved-flash {
    0%, 57% { background: rgba(76, 95, 60, 0.28); }
    100%    { background: transparent; }
  }
  .lists-table >>> .v-data-table__wrapper {
    height: calc(100vh - var(--offset) - 122px) !important;
    overflow-y: auto;
    overflow-x: auto;
  }
  /* 28 May 2026 JCH: Solid background + z-index so the sticky header isn't bled through by cell inputs. */
  .lists-table >>> .v-data-table__wrapper thead th {
    background: #fff;
    z-index: 3;
  }
  .lists-cell-input { font-size: 0.875rem; padding-top: 0 !important; margin-top: 0 !important; }
  .lists-cell-input >>> input { padding: 2px 0 !important; }
  .lists-cell-input--sort >>> input { text-align: right; }   /* desktop: right-aligned numbers */
  @media (max-width: 599px) {
    .lists-toolbar >>> .v-input--dense input,
    .lists-toolbar >>> .v-input--dense .v-label,
    .lists-toolbar >>> .v-input--dense .v-messages {
      font-size: 0.8125rem !important;
    }
    .lists-table >>> .v-data-table__wrapper td,
    .lists-table >>> .v-data-table__wrapper th { padding: 0 4px !important; }
    .lists-table >>> .v-data-table__wrapper td:first-child,
    .lists-table >>> .v-data-table__wrapper th:first-child { padding-left: 12px !important; }
    .lists-cell-input { font-size: 0.75rem; }
    .lists-cell-input--sort >>> input::-webkit-outer-spin-button,
    .lists-cell-input--sort >>> input::-webkit-inner-spin-button { -webkit-appearance: none; appearance: none; margin: 0; }
    .lists-cell-input--sort >>> input { -moz-appearance: textfield; appearance: textfield; text-align: center; }
  }
</style>
